import { useCallback } from 'react';

import {
  trackQuestionAnswered,
  trackQuestionSkipped,
} from '@/features/advisor-onboarding/questionnaire/lib/analytics';

import type { QuestionnaireAnswers } from '@shared';

interface UseQuestionSubmitHandlersOptions {
  questionId: string;
  onSubmit: (patch: Partial<QuestionnaireAnswers>) => Promise<void> | void;
  onAdvance: (patch?: Partial<QuestionnaireAnswers>) => void;
}

export interface QuestionSubmitHandlers {
  submitAnswer: (patch: Partial<QuestionnaireAnswers>) => Promise<void>;
  submitAnswerWithoutAdvance: (patch: Partial<QuestionnaireAnswers>) => Promise<void>;
  submitSkip: () => Promise<void>;
  advance: (patch?: Partial<QuestionnaireAnswers>) => void;
}

export function useQuestionSubmitHandlers({
  questionId,
  onSubmit,
  onAdvance,
}: UseQuestionSubmitHandlersOptions): QuestionSubmitHandlers {
  const submitAnswerWithoutAdvance = useCallback(
    async (patch: Partial<QuestionnaireAnswers>) => {
      trackQuestionAnswered(questionId, patch);
      await onSubmit(patch);
    },
    [questionId, onSubmit],
  );

  const submitAnswer = useCallback(
    async (patch: Partial<QuestionnaireAnswers>) => {
      await submitAnswerWithoutAdvance(patch);
      onAdvance(patch);
    },
    [submitAnswerWithoutAdvance, onAdvance],
  );

  const submitSkip = useCallback(async () => {
    trackQuestionSkipped(questionId);
    await onSubmit({});
    onAdvance({});
  }, [questionId, onSubmit, onAdvance]);

  const advance = useCallback(
    (patch?: Partial<QuestionnaireAnswers>) => {
      onAdvance(patch);
    },
    [onAdvance],
  );

  return { submitAnswer, submitAnswerWithoutAdvance, submitSkip, advance };
}
